import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, query, onSnapshot, orderBy } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { FileText, ChevronRight, User, Clock } from "lucide-react";

export default function ATTApplicationsTable() {
    const navigate = useNavigate();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Real-time listener for ATT applications
        const q = query(collection(db, "attApplications"), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setApplications(data);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching applications:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const getStatusStyles = (status) => {
        switch (status?.toLowerCase()) {
            case "approved": return "bg-emerald-100 text-emerald-700 border-emerald-200";
            case "rejected": return "bg-red-100 text-red-700 border-red-200";
            case "needs_fix": return "bg-orange-100 text-orange-700 border-orange-200";
            case "pending": return "bg-blue-100 text-blue-700 border-blue-200";
            case "ready_for_abf": return "bg-purple-100 text-purple-700 border-purple-200";
            default: return "bg-slate-100 text-slate-600 border-slate-200";
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-900"></div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <table className="w-full text-left">
                <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                        <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Client</th>
                        <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</th>
                        <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Progress</th>
                        <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Action</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                    {applications.map((app) => (
                        <tr key={app.id} className="hover:bg-slate-50/50 transition-colors group">
                            <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-navy-50 flex items-center justify-center text-navy-900 shadow-sm border border-navy-100 group-hover:scale-110 transition-transform font-bold text-xs">
                                        {app.clientName?.charAt(0) || <User size={18} />}
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-navy-900">{app.clientName || 'Unnamed Client'}</p>
                                        <p className="text-[10px] text-slate-400 flex items-center gap-1">
                                            <Clock size={10} /> {app.createdAt?.seconds ? new Date(app.createdAt.seconds * 1000).toLocaleDateString() : 'Recent'}
                                        </p>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-[10px] font-bold border ${getStatusStyles(app.status)}`}>
                                    {app.status || 'Pending'}
                                </span>
                            </td>
                            <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden w-24">
                                        <div
                                            className="h-full bg-navy-900 rounded-full transition-all duration-1000"
                                            style={{ width: `${app.progress || 0}%` }}
                                        />
                                    </div>
                                    <span className="text-[11px] font-bold text-navy-900">{app.progress || 0}%</span>
                                </div>
                            </td>
                            <td className="px-6 py-4 text-right">
                                <button
                                    onClick={() => navigate(`/agent/att-review/${app.id}`)}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-navy-900 hover:border-navy-900 hover:shadow-md transition-all active:scale-95"
                                >
                                    Review <ChevronRight size={14} />
                                </button>
                            </td>
                        </tr>
                    ))}
                    {applications.length === 0 && (
                        <tr>
                            <td colSpan="4" className="px-6 py-12 text-center">
                                <FileText size={48} className="mx-auto text-slate-200 mb-4" />
                                <p className="text-sm text-slate-500 font-medium">No pending ATT applications found.</p>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}
